/**
 * Main Application Store using Zustand
 * Replaces the old RootStore pattern with modern React state management
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { useShallow } from 'zustand/react/shallow';
import type { ISettings } from '../../types/models/ISettings';
import type { ApiGameSettings } from '../../types/api/api.types';
import type { IBalance } from '../../types/models/IBalance';
import type { IExternalBalance } from '../../types/external/IExternalBalance';
import type ISectionSettings from '../../types/models/ISectionSettings';
import { LOBBY_SECTION_NAME, SECTION_NAMES, formatCurrencyValue } from '@repo/common';
import type { CurrentPlayersSlice } from '../currentPlayers/currentPlayersSlice';
import { createCurrentPlayersSlice } from '../currentPlayers/currentPlayersSlice';
import { externalCommunication } from '../../core/services/communication/ExternalCommunication';
import { initSentry, SentryProjects } from '../../core/utils/sentry-utils';
import packageJson from '../../../package.json';
import { userDataTracker } from '../../core/services/UserDataTracker';

import { EGT } from '../../constants';
import { PRIZE_POP_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns';

const SECTIONS_ALLOWED_WITHOUT_GAMES = new Set<string>([
  SECTION_NAMES.TOURNAMENTS,
  SECTION_NAMES.TOP,
  SECTION_NAMES.PRIZE_POP,
  SECTION_NAMES.TOP_WIN_GAMES,
  SECTION_NAMES.REBATE,
]);

interface AppState extends CurrentPlayersSlice {
  // Settings
  settings: ISettings;
  updateSettings: (settings: Partial<ISettings>) => void;

  // Balance
  balance: IBalance;
  updateBalance: (balance: IExternalBalance) => void;

  // Lobby sections
  sections: ISectionSettings[];
  selectedSection: string;
  viewMode: 'multi' | 'single';
  setSections: (sections: ISectionSettings[]) => void;
  setSelectedSection: (section: string) => void;
  setViewMode: (mode: 'multi' | 'single') => void;

  // Supported games (for tournament parsing)
  supportedGames: { [gameKey: string]: ApiGameSettings };
  setSupportedGames: (games: { [gameKey: string]: ApiGameSettings }) => void;

  // UI State
  isLoading: boolean;
  settingsRequestDone: boolean;
  isVisible: boolean;
  isIdle: boolean;
  setLoading: (loading: boolean) => void;
  setSettingsRequestDone: (done: boolean) => void;
  setVisible: (visible: boolean) => void;
  setIdle: (idle: boolean) => void;

  // Supported languages
  supportedLanguages: string[];
  setSupportedLanguages: (languages: string[]) => void;

  // Initialization trigger
  shouldInitialize: boolean;
  triggerInitialization: () => void;

  // Settings Modal
  showSettingsModal: boolean;
  setShowSettingsModal: (show: boolean) => void;

  // Search functionality
  isSearchActive: boolean;
  searchQuery: string;
  viewModeBeforeSearch: 'multi' | 'single' | null;
  setSearchActive: (active: boolean) => void;
  setSearchQuery: (query: string) => void;

  // Ingame support
  isIngameCampaignOnlyOpened: boolean;
  setIsIngameCampaignOnlyOpened: (isOpened: boolean) => void;
  awaitingSettingsAfterHide: boolean;
  setAwaitingSettingsAfterHide: (awaiting: boolean) => void;

  // Exit functionality
  handleExit: () => void;
}

const DEFAULT_SETTINGS: ISettings = {
  host: '',
  apiHost: '',
  redirectHost: '',
  assetsHost: '',
  assetsHash: '',
  playerId: '',
  gameKey: '',
  languageCode: 'EN',
  theme: 'default',
  mute: false,
  bgMusicMute: false,
  isSettingsModalOpen: false,
  isSearchOpen: false,
  visible: false,
  closeUrl: '',
  sendOpenLobbyEvent: false,
  singleView: '',
  tournamentHost: '',
  prizePopHost: '',
  rebateHost: '',
  sessionKey: '',
  mode: 'desktop' as const,
  openType: 'fullscreen' as const,
  mountLobby: true,
  isAggregator: false,
  campaigns: [],
  restrictions: [],
};

const DEFAULT_BALANCE: IBalance = {
  units: 100,
  currency: 'EGT',
  balance: 50000,
  formattedBalance: '500.00',
};

const parseBalance = (externalBalance: IExternalBalance): IBalance => ({
  balance: externalBalance.balance,
  formattedBalance: formatCurrencyValue(externalBalance.balance, externalBalance.units),
  units: externalBalance.units,
  currency: externalBalance.currency,
});

// Track Sentry initialization to prevent multiple initializations
let sentryInitialized = false;

// Initialize Sentry when lobby becomes visible
const initializeSentryForLobby = () => {
  // Only initialize Sentry once
  if (sentryInitialized) return;

  sentryInitialized = true;

  // Initialize Sentry based on SSI configuration
  const ssi = window.lobbySsi;
  if (ssi && typeof ssi.sentryEnvironment === 'string') {
    const sentryEnvironment = ssi.sentryEnvironment;
    const sentryProject =
      SentryProjects[sentryEnvironment.toUpperCase() as keyof typeof SentryProjects] || SentryProjects.DEV;
    const sentryClient = initSentry(packageJson.version, sentryProject);

    // Initialize Session Service
    import('../../core/services/SessionService').then(({ sessionService }) => {
      sessionService.init(sentryClient);
    });
  }
};

export const useAppStore = createWithEqualityFn<AppState>()((set, get) => ({
  // Initial state
  settings: DEFAULT_SETTINGS,
  balance: DEFAULT_BALANCE,
  sections: [],
  selectedSection: LOBBY_SECTION_NAME,
  viewMode: 'multi' as const,
  supportedGames: {},
  isLoading: false,
  settingsRequestDone: false,
  isVisible: false,
  isIdle: false,
  supportedLanguages: ['BG', 'EN'],
  shouldInitialize: false,
  showSettingsModal: false,
  isSearchActive: false,
  searchQuery: '',
  isIngameCampaignOnlyOpened: false,
  awaitingSettingsAfterHide: false,

  viewModeBeforeSearch: null,

  // Current Players slice
  ...createCurrentPlayersSlice(set, get),

  // Settings actions
  updateSettings: (newSettings) => {
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
    }));

    // Track user settings changes for Sentry
    const updatedSettings = get().settings;
    const soundEnabled = updatedSettings.mute === false;
    userDataTracker.setSettings(soundEnabled, updatedSettings.languageCode || '');

    // Track user ID and session for Sentry
    // Always sync current playerId and sessionKey to ensure Sentry has the latest values
    // This handles cases where playerId comes from initial settings, parent updates, or SSI
    userDataTracker.setUserData(updatedSettings?.playerId || '', updatedSettings?.sessionKey || '');
  },

  // Balance actions
  updateBalance: (externalBalance) => set({ balance: parseBalance(externalBalance) }),

  // Sections actions, filter sections only with games (except TOURNAMENTS which use API)
  // minLimit controls how many games a section must have to be shown (default 1).
  // Values < 1 are ignored (treated as 1). If minLimit is 2, sections with 0 or 1 game are hidden.
  setSections: (sections) =>
    set({
      sections: sections.filter((section) => {
        const minLimit = section?.minLimit != null && section?.minLimit >= 1 ? section?.minLimit : 1;
        return section.games.length >= minLimit || SECTIONS_ALLOWED_WITHOUT_GAMES.has(section.name);
      }),
    }),

  setSupportedGames: (games) => set({ supportedGames: games }),

  setSelectedSection: (section) => {
    set({ selectedSection: section });
  },

  setViewMode: (mode: 'multi' | 'single') => {
    // Sound is already played in Navigation component's handleTabClick
    // Removed duplicate sound call to fix double sound issue
    set({ viewMode: mode });
  },

  // UI actions
  setLoading: (loading) => set({ isLoading: loading }),

  setSettingsRequestDone: (done) => set({ settingsRequestDone: done }),

  setVisible: (visible) => {
    set({ isVisible: visible });

    // Initialize Sentry when lobby becomes visible for the first time
    if (visible) {
      initializeSentryForLobby();
    }
  },

  setIdle: (idle) => set({ isIdle: idle }),

  setSupportedLanguages: (languages) => set({ supportedLanguages: languages }),

  // Initialization trigger (for mountLobby: false scenarios)
  triggerInitialization: () => set({ shouldInitialize: true }),

  // Settings Modal actions
  setShowSettingsModal: (show) => set({ showSettingsModal: show }),

  // Search actions
  setSearchActive: (active) => {
    const currentState = get();
    if (active) {
      // Opening search: save current viewMode and switch to single
      set({
        isSearchActive: true,
        viewModeBeforeSearch: currentState.viewMode,
        viewMode: 'single',
      });
    } else {
      // Closing search: restore previous viewMode
      set({
        isSearchActive: false,
        viewMode: currentState.viewModeBeforeSearch || currentState.viewMode,
        viewModeBeforeSearch: null,
      });
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),

  // Ingame actions
  setIsIngameCampaignOnlyOpened: (isOpened: boolean) => set({ isIngameCampaignOnlyOpened: isOpened }),
  setAwaitingSettingsAfterHide: (awaiting: boolean) => set({ awaitingSettingsAfterHide: awaiting }),

  // Exit functionality
  handleExit: () => {
    // Send exit command to IFrameApplication
    externalCommunication.sendExit();
  },
}));

/**
 * Derives the selectedSection/viewMode that correspond to a given
 * campaignOnly setting. Used to set the view state in the
 * same tick as a settings update, so the lobby renders the right view
 * (campaign single view vs. multi-view lobby) immediately, without a
 * one-frame flash of the previous view.
 */
type CampaignViewState = { selectedSection: string; viewMode: 'multi' | 'single' };

const CAMPAIGN_ONLY_VIEW_STATE: Record<string, CampaignViewState> = {
  [TOURNAMENTS_CAMPAIGN_TYPE]: { selectedSection: SECTION_NAMES.TOURNAMENTS, viewMode: 'single' },
  [PRIZE_POP_CAMPAIGN_TYPE]: { selectedSection: SECTION_NAMES.PRIZE_POP, viewMode: 'single' },
  [REBATE_CAMPAIGN_TYPE]: { selectedSection: SECTION_NAMES.REBATE, viewMode: 'single' },
};

export const deriveCampaignViewState = (
  settings: Pick<ISettings, 'campaignOnly'>,
): CampaignViewState => {
  const type = settings.campaignOnly?.type;
  if (type && CAMPAIGN_ONLY_VIEW_STATE[type]) {
    return CAMPAIGN_ONLY_VIEW_STATE[type];
  }

  return { selectedSection: LOBBY_SECTION_NAME, viewMode: 'multi' };
};

// Optimized selector hooks with shallow comparison for better performance
export const useSettings = () => useAppStore((state) => state.settings);
export const useBalance = () => useAppStore((state) => state.balance);

export const useSections = () =>
  useAppStore(
    useShallow((state) => ({
      sections: state.sections,
      selectedSection: state.selectedSection,
      viewMode: state.viewMode,
    })),
  );
// Memoized UI state selector
export const useUIState = () =>
  useAppStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      settingsRequestDone: state.settingsRequestDone,
      isVisible: state.isVisible,
      isIdle: state.isIdle,
      awaitingSettingsAfterHide: state.awaitingSettingsAfterHide,
    })),
  );

// Specific selectors for individual pieces of state
export const useSelectedSection = () => useAppStore((state) => state.selectedSection);
export const setSelectedSection = useAppStore.getState().setSelectedSection;

export const useViewMode = () => useAppStore((state) => state.viewMode);
export const setViewMode = useAppStore.getState().setViewMode;

export const useIsIdle = () => useAppStore((state) => state.isIdle);

export const setIsIngameCampaignOnlyOpened = useAppStore.getState().setIsIngameCampaignOnlyOpened;

// Currency-specific selectors to avoid re-renders when other balance properties change
export const useCurrency = () => useAppStore((state) => state.balance.currency);
// export const useFormattedBalance = () => useAppStore((state) => state.balance.formattedBalance);

// Section-specific selectors for performance
// export const useSectionByName = (sectionName: string) =>
//   useAppStore((state) => state.sections.find((section) => section.name === sectionName));

// export const useGamesBySectionName = (sectionName: string) =>
//   useAppStore((state) => {
//     const section = state.sections.find((s) => s.name === sectionName);
//     return section?.games || [];
//   });

// Non-reactive helper: read a section's game keys outside of React render.
export const getSectionGameKeys = (sectionName: string): string[] => {
  const section = useAppStore.getState().sections.find((s) => s.name === sectionName);
  return section?.games.map((game) => game.gameKey) ?? [];
};

// Optimized selector for language code
export const useLanguageCode = () => useAppStore((state) => state.settings.languageCode);
export const useDeviceMode = () => useAppStore((state) => state.settings.mode);
export const useDemo = () => useAppStore((state) => state.balance?.currency?.toUpperCase() === EGT);

// Camapaign only selector
export const useCampaignOnlyType = () => useAppStore((state) => state.settings.campaignOnly?.type);

// PERFORMANCE OPTIMIZED: Assets-specific selector to prevent re-renders on unrelated settings changes
export const useAssetsConfig = () =>
  useAppStore(
    useShallow((state) => ({
      assetsHost: state.settings.assetsHost,
      assetsHash: state.settings.assetsHash,
    })),
  );

// Current players selectors
// Section availability selectors
export const useHasPrizePopSection = () =>
  useAppStore((state) => state.sections.some((s) => s.name === SECTION_NAMES.PRIZE_POP));
// export const useHasTournamentSection = () =>
//   useAppStore((state) => state.sections.some((s) => s.name === SECTION_NAMES.TOURNAMENTS));
// export const useHasRebateSection = () =>
//   useAppStore((state) => state.sections.some((s) => s.name === SECTION_NAMES.REBATE));

export const usePlayerCountForGame = (gameKey: string) => useAppStore((state) => state.getPlayerCountForGame(gameKey));
export const useFetchCurrentPlayers = () => useAppStore((state) => state.fetchCurrentPlayers);
